import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../api/client'
import { createUnknownOwner, unknownOwnerPanelQuery } from '../api/settingsOwners'
import { Modal } from '../ui/Modal'
import { SelectPicker } from '../ui/SelectPicker'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'

/**
 * "Create owner" pentru un contact necunoscut - macheta
 * `views/version-3/quick_sidebar/unknown_contact/index.php` si scriptul ei
 * (`public/js/views/quick_sidebar/unknown_contact/index-1.js`).
 *
 * In portal se deschidea in sertarul din dreapta (`page-quick-sidebar`), la
 * clic pe adresa necunoscuta din lista de tichete (`.quick-sidebar-unknown`).
 * Sertarul acela nu s-a mutat inca (`#user-sidebar` e un div gol in Shell),
 * deci formularul - acelasi, camp cu camp: Email fix, Firstname*, Lastname*,
 * Phone, Company* - se deschide intr-o fereastra. Toastr-ele sunt cele vechi:
 * "Owner created successfully" / textul serverului la refuz.
 */
export function UnknownOwnerModal({ contactId, onClose }: { contactId: number; onClose(): void }) {
  const panel = useQuery(unknownOwnerPanelQuery(contactId))
  const client = useQueryClient()
  const [firstname, setFirstname] = useState('')
  const [lastname, setLastname] = useState('')
  const [phone, setPhone] = useState('')
  const [company, setCompany] = useState('')
  const [busy, setBusy] = useState(false)

  /* `<select required>` fara optiune goala: prima firma e aleasa din start. */
  useEffect(() => {
    if (company === '' && panel.data && panel.data.companies.length > 0) setCompany(String(panel.data.companies[0].id))
  }, [panel.data, company])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void createUnknownOwner(contactId, { firstname, lastname, phone, company })
      .then(() => {
        setBusy(false)
        toast('Owner created successfully', 'Create owner')
        void client.invalidateQueries({ queryKey: ['easeedesk'] })
        onClose()
      })
      .catch((err: unknown) => {
        setBusy(false)
        toast(err instanceof ApiError && err.message ? err.message : 'Something went wrong.', 'Create owner', 'error')
      })
  }

  return (
    <Modal id="unknownOwnerModal" open onClose={onClose} title="Create owner" labelledBy="unknownOwnerModalLabel">
      {panel.error instanceof ApiError ? (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {panel.error.message || 'Not found.'}
        </div>
      ) : !panel.data ? (
        <Spinner />
      ) : (
        <form className="quick-create-owner" onSubmit={submit}>
          <div className="form-group">
            <label>Email</label>
            <p className="form-control-static">
              <i className="far fa-envelope" aria-hidden="true"></i> {panel.data.contact.address}
            </p>
          </div>
          <div className="form-group">
            <label htmlFor="unknown-firstname">
              Firstname <span className="required">*</span>
            </label>
            <input id="unknown-firstname" className="form-control input-sm" type="text" name="firstname" required value={firstname} onChange={(e) => setFirstname(e.target.value)} />
          </div>
          <div className="form-group">
            <label htmlFor="unknown-lastname">
              Lastname <span className="required">*</span>
            </label>
            <input id="unknown-lastname" className="form-control input-sm" type="text" name="lastname" required value={lastname} onChange={(e) => setLastname(e.target.value)} />
          </div>
          <div className="form-group">
            <label htmlFor="unknown-phone">Phone</label>
            <input id="unknown-phone" className="form-control input-sm" type="text" name="phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="form-group">
            <label>
              Company <span className="required">*</span>
            </label>
            <SelectPicker
              id="unknown-company"
              className="form-control input-sm select-pick"
              name="company"
              title="Company"
              options={panel.data.companies.map((c) => ({ value: String(c.id), label: c.name }))}
              value={company}
              onChange={setCompany}
            />
          </div>
          <div className="form-group align-right">
            <button type="submit" className="save-quick-owner btn btn-sm btn-primary" disabled={busy || company === ''}>
              Save
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}
